import { SectionShell } from "@/components/design-system/SectionShell";
import { SubNav } from "@/components/SubNav";

const code = `import { SubNav } from "@/components/SubNav";

// Inside a page, as the page header's \`tabs\` slot:
<PageHeader
  title="Acme Inc."
  description="Workspace settings and billing."
  tabs={
    <SubNav
      ariaLabel="Workspace sections"
      items={[
        { href: "/workspace", label: "Overview" },
        { href: "/workspace/members", label: "Members" },
        { href: "/workspace/billing", label: "Billing" },
      ]}
      actions={<a href="/workspace/audit">View audit log</a>}
    />
  }
/>

// A tab is active when the current URL equals its href or sits beneath it, so
// a drill-down keeps its parent tab lit. Pass \`active\` to override that — a
// boolean, or a predicate over the current URL when the tab owns a route that
// does not nest under its href:
{ href: "/runs", label: "History", active: (url) => url.startsWith("/runs") }`;

// The preview drives `active` by hand and points every tab back at this
// section, so the reference page demonstrates the real component without
// navigating away from it.
const ITEMS = [
  { href: "#sub-navigation", label: "Overview", active: true },
  { href: "#sub-navigation-activity", label: "Activity", active: false },
  { href: "#sub-navigation-members", label: "Members", active: false },
  { href: "#sub-navigation-billing", label: "Billing", active: false },
];

export function SubNavSection() {
  return (
    <SectionShell
      id="sub-navigation"
      title="Sub navigation"
      description={
        <>
          Horizontal tab list for navigating within a single section of the
          app (settings sub-pages, project tabs, profile sections). The
          active tab's underline merges with the row's bottom hairline. An
          optional right-side slot accepts secondary links, filters, search
          fields, or small actions. Below the <code>md</code> breakpoint,
          the tab strip collapses into a full-width dropdown that surfaces
          the active tab — tap to switch sections without horizontal
          scrolling or wrapped rows. The previews below are the real{" "}
          <code>SubNav</code> component: narrow the window past{" "}
          <code>md</code> to watch them become dropdowns.
        </>
      }
      whenToUse={
        <ul>
          <li>When a page has 3+ sibling sub-views with stable structure.</li>
          <li>Append to a Page header (see the "with tabs" variant).</li>
          <li>Right-side slot is optional — leave empty when not needed.</li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>For 1–2 sub-pages — just link them inline in the page body.</li>
          <li>For dynamic, list-of-things navigation — use a Listing.</li>
          <li>For top-level app destinations — those belong in main navigation.</li>
        </ul>
      }
      preview={
        <div className="space-y-8">
          <div>
            <p className="mb-2 text-xs uppercase tracking-wider text-ink-muted">
              Tabs only
            </p>
            <SubNav ariaLabel="Example sections" items={ITEMS} />
          </div>

          <div>
            <p className="mb-2 text-xs uppercase tracking-wider text-ink-muted">
              With right-side content
            </p>
            <SubNav
              ariaLabel="Example sections with actions"
              items={ITEMS.slice(0, 3)}
              actions={
                <span className="text-sm text-ink-muted">View all</span>
              }
            />
          </div>
        </div>
      }
      code={code}
      options={
        <ul className="list-disc pl-5">
          <li>
            <strong>Mobile breakpoint</strong>: tabs collapse into a
            full-width <code>&lt;Select&gt;</code> below the <code>md</code>{" "}
            breakpoint. The dropdown's current value is the active tab, and
            choosing another one visits it — so users can switch sections
            without horizontal scrolling.
          </li>
          <li>
            <strong>Active state</strong>:{" "}
            <code>border-b-2 border-accent text-accent-display font-medium</code>{" "}
            on the active tab; <code>-mb-px</code> overlaps the parent's
            hairline so the underlines merge into one line. The active tab
            also carries <code>aria-current="page"</code>.
          </li>
          <li>
            <strong>Right-side slot</strong>: pass any node as{" "}
            <code>actions</code> — links, filters, search fields, small icon
            buttons, or a picker. On desktop the slot is{" "}
            <code>self-stretch</code>, so it fills the strip's height and
            centres its contents between the two hairlines — a control as tall
            as the row sits evenly, and text lands on the tab labels. On mobile
            it stacks <em>above</em> the dropdown so the dropdown stays truly
            full-width.
          </li>
          <li>
            <strong>Pair with Page header</strong>: pass it as the{" "}
            <code>PageHeader</code> component's <code>tabs</code> prop. Result:
            two horizontal lines.
          </li>
          <li>
            <strong>Real usage</strong>: the Automations module wraps this in{" "}
            <code>AutomationsSubNav</code> — Overview and History, with the
            organization picker in the right-hand slot.
          </li>
        </ul>
      }
    />
  );
}
